import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Coins } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import PageShell, { PageHead } from "@/components/pages/PageShell";
import { Join, Payment, TrollKroner, Ways } from "@/components/hosts/HostParts";
import { HOST_SIGNUP } from "@/config/business";

const Buttons = ({ prefix, center }) => {
  const { t } = useLang();
  const h = t.pages.hosts;
  return (
    <div className={`mt-8 flex flex-wrap items-center gap-3 ${center ? "justify-center" : ""}`}>
      <Link to={HOST_SIGNUP} data-testid={`${prefix}-register`} className="inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark text-parchment px-7 py-3.5 font-extrabold shadow-journal transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
        {h.ctaHost} <ArrowRight className="w-4 h-4" />
      </Link>
      <Link to="/get-app" data-testid={`${prefix}-get-app`} className="inline-flex items-center gap-2 rounded-full border-2 border-ink px-6 py-3 font-extrabold text-ink hover:bg-ink hover:text-parchment transition-colors">
        {h.cta}
      </Link>
      <Link to="/supporter" data-testid={`${prefix}-partner`} className="basis-full text-sm font-bold text-moss underline decoration-moss/40 underline-offset-4 hover:decoration-moss transition-[text-decoration-color]">
        {h.ctaPartner}
      </Link>
    </div>
  );
};

const HostsHero = () => {
  const { t } = useLang();
  const h = t.pages.hosts;
  return (
    <div className="grid lg:grid-cols-12 gap-12 items-center">
      <div className="lg:col-span-7">
        <PageHead kicker={h.kicker} title={h.title} lead={h.lead}>
          <ul className="mt-7 flex flex-wrap gap-2" data-testid="hosts-badges">
            {h.badges.map((b) => (
              <li key={b} className="inline-flex items-center gap-1.5 rounded-full bg-moss/10 text-moss px-3 py-1.5 text-sm font-extrabold"><Check className="w-4 h-4" /> {b}</li>
            ))}
          </ul>
          <Buttons prefix="hosts-hero" />
        </PageHead>
      </div>
      <div className="lg:col-span-5 relative">
        <div className="rounded-[20px] p-2.5 bg-paper border border-line shadow-journal-lg rotate-[1.2deg]">
          <img src="/business/cafes.webp" alt={h.ways[0].title} className="w-full h-[320px] md:h-[440px] object-cover rounded-[14px]" />
        </div>
        <div className="absolute -left-3 bottom-10 inline-flex items-center gap-2 rounded-full bg-amber text-parchment px-4 py-2 text-sm font-extrabold shadow-journal-lg">
          <Coins className="w-4 h-4" /> {h.tkTitle}
        </div>
      </div>
    </div>
  );
};

export default function HostsPage() {
  const { t } = useLang();
  const h = t.pages.hosts;
  return (
    <PageShell testId="hosts-page">
      <div className="space-y-24 md:space-y-32">
        <HostsHero />
        <Ways />
        <Join />
        <TrollKroner />
        <Payment />
        <section data-testid="hosts-cta" className="rounded-[2rem] border-2 border-dashed border-moss/40 bg-moss/5 p-8 md:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink">{h.ctaTitle}</h2>
          <p className="mt-4 text-ink-muted max-w-2xl mx-auto leading-relaxed">{h.ctaBody}</p>
          <div className="flex justify-center"><Buttons prefix="hosts-cta" center /></div>
        </section>
      </div>
    </PageShell>
  );
}
